import {
    BadgeColor,
    BadgeShape,
    BadgeSize,
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExBadge,
    ExButton,
} from '@boomi/exosphere';
import { BREAKPOINT_OPERATORS } from '../../../../../constants/debugger';
import translations from '../../../../../translations';
import type {
    BreakpointOperator,
    Breakpoint as BreakpointType,
} from '../../../../../types/debugger';
import type { ValueElementIdReferenceAPI } from '../../../../../types/value';
import ButtonIcon from '../../../../buttons/ButtonIcon';
import Table, { type TableColumnList } from '../../../../generic/Table';
import { getValueName, getValueType } from '../../../../values/selector/value-selector-utils';
import { useDebugDispatch, useDebugState } from '../../DebugProvider';
import EmptyBreakpointList from './EmptyBreakpointList';

interface Props {
    addNew: () => void;
    edit: (breakpoint: BreakpointType) => void;
    screen?: string;
}

const BreakpointList = ({ addNew, edit }: Props) => {
    const { breakpoints, callStack } = useDebugState();
    const dispatch = useDebugDispatch();

    const selectedCallStackItem = callStack.find((csi) => csi.isSelected);

    // Assertions are only evaluated against the call stack item currently selected, so without a
    // selection we can't claim an assertion was missed, only that nothing has been checked yet.
    const getHitStatus = (breakpointId: string) => {
        if (!selectedCallStackItem) {
            return { label: 'No step selected', color: BadgeColor.GRAY };
        }

        const isHit = !!selectedCallStackItem.breakpointsHit?.some(
            (bph) => bph.breakpointId === breakpointId,
        );

        return isHit
            ? { label: 'Hit', color: BadgeColor.GREEN }
            : { label: 'Not hit', color: BadgeColor.GRAY };
    };

    const removeBreakpoint = (breakpointId: string) => {
        dispatch({ type: 'removeBreakpoint', breakpointId });
    };

    const getOperatorLabel = (breakpoint: BreakpointType) =>
        (
            Object.values(BREAKPOINT_OPERATORS).find(
                (rule) => rule.criteria === breakpoint.operator,
            ) as BreakpointOperator
        ).label;

    const columns: TableColumnList<BreakpointType> = [
        {
            renderHeader: () => 'Status',
            renderCell: ({ item: { id } }) => {
                const { label, color } = getHitStatus(id);

                return (
                    <ExBadge
                        data-testid={`${id}-is-hit`}
                        size={BadgeSize.SMALL}
                        shape={BadgeShape.ROUND}
                        color={color}
                    >
                        {label}
                    </ExBadge>
                );
            },
            size: '10rem',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_value,
            renderCell: ({ item: { value } }) => (
                <>
                    <div>{getValueName(value as ValueElementIdReferenceAPI)}</div>
                    <div className="quiet">{getValueType(value as ValueElementIdReferenceAPI)}</div>
                </>
            ),
            cellClassName: 'generic-cell-overflow',
        },
        {
            renderHeader: () => 'Operator',
            renderCell: ({ item }) => getOperatorLabel(item),
        },
        {
            renderHeader: () => 'Expected Value',
            renderCell: ({ item: { expectedContentValue } }) => expectedContentValue,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <div className="flex">
                    <ButtonIcon
                        testId={`edit-${item.id}`}
                        glyph="Edit"
                        title="Edit"
                        aria-label="Edit"
                        iconClass="icon-medium"
                        onClick={() => edit(item)}
                    />
                    <ButtonIcon
                        testId={`remove-${item.id}`}
                        className="margin-left-small danger"
                        glyph="Delete"
                        title="Delete"
                        aria-label="Delete"
                        iconClass="icon-medium"
                        onClick={() => removeBreakpoint(item.id)}
                    />
                </div>
            ),
            size: '6rem',
        },
    ];

    return (
        <>
            {breakpoints.length ? (
                <div data-testid="breakpoint-list">
                    <Table columns={columns} items={breakpoints} rowKeyGenerator={(bp) => bp.id} />
                    <div className="flex margin-top">
                        <ExButton
                            flavor={ButtonFlavor.BRANDED}
                            type={ButtonType.SECONDARY}
                            size={ButtonSize.DEFAULT}
                            onClick={addNew}
                        >
                            Add Assertion
                        </ExButton>
                    </div>
                </div>
            ) : (
                <EmptyBreakpointList addBreakpoint={addNew} />
            )}
        </>
    );
};

export default BreakpointList;
